// REST client for the Underwriting AI Inbox (AgentMail) on the AI API.
// The AgentMail key and webhook live on the server; the browser only manages the inbox.

export interface CustomRequestType {
  id: number;
  title: string;
  tag: string;
}

export interface InboxStatus {
  enabled: boolean;
  address: string | null;
  enabledAt: string | null;
  customTypes: CustomRequestType[];
}

export interface InboxMail {
  from: string;
  body: string;
}

export interface InboxAttachment {
  filename: string;
  size: number;
  contentType: string;
}

export interface InboxConversation {
  id: string;
  subject: string;
  sender: string;
  /** ISO timestamp of the latest activity (UTC, no zone suffix). */
  date: string;
  status: 'Replied' | 'Failed' | 'Processing' | 'Received' | string;
  /** The mails received on the thread, as sent: no reply from us and nothing read out of the files. */
  mails: InboxMail[];
  attachments: InboxAttachment[];
}

/** A failed call, with a message that is safe to show and the HTTP status for callers that branch on it. */
export class AgentMailApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'AgentMailApiError';
  }
}

const TIMEOUT_MS = 30000;

function apiBase(): string {
  return process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url ?? '';
}

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
  };
}

function messageFor(status: number, detail?: string): string {
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'Only a carrier admin can manage the Underwriting AI Inbox.';
  if (status === 503) return 'The Underwriting AI Inbox isn’t set up on this server yet.';
  return detail || 'Something went wrong. Please try again.';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, { ...init, headers: authHeaders(), signal: controller.signal });
  } catch {
    throw new AgentMailApiError("Can't reach the server. Check your connection and try again.");
  } finally {
    window.clearTimeout(timer);
  }

  if (!res.ok) {
    let detail: string | undefined;
    try {
      const body = await res.json();
      // FastAPI sends { detail: string }; validation errors send a list, which isn't worth showing.
      if (typeof body?.detail === 'string') detail = body.detail;
    } catch { /* no JSON body */ }
    throw new AgentMailApiError(messageFor(res.status, detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const getInboxStatus = () => request<InboxStatus>('/agentmail/inbox');

/** Idempotent: enabling an already-enabled inbox returns it unchanged. */
export const enableInbox = () => request<InboxStatus>('/agentmail/inbox', { method: 'POST' });

export const deleteInbox = () => request<InboxStatus>('/agentmail/inbox', { method: 'DELETE' });

export const addRequestType = (title: string) =>
  request<CustomRequestType>('/agentmail/request-types', { method: 'POST', body: JSON.stringify({ title }) });

export const deleteRequestType = (id: number) =>
  request<void>(`/agentmail/request-types/${id}`, { method: 'DELETE' });

export const getInboxHistory = () => request<InboxConversation[]>('/agentmail/history');

/** 117760 -> "115 KB", the way a mail client shows an attachment's size. */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "2026-10-26T17:52:00" (UTC, no suffix) -> "Oct 26, 5:52 PM" in the viewer's timezone. */
export function formatInboxDate(iso: string): string {
  if (!iso) return '';
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
